import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Input } from '@mui/material';
import type React from 'react';
import type { ChangeEventHandler } from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import type { ConfirmState } from '../../reducer.js';

const ConfirmDialog = styled(Dialog)`
  .MuiPaper-root {
    min-width: ${({ theme }) => theme.spacing(40)};
  }
`;

const DeleteButton = styled(Button)`
  background: ${({ theme }) => theme.palette.error.main};

  :hover {
    background: ${({ theme }) => theme.palette.error.dark};
  }
`;

const ConfirmInput = styled(Input)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const StyledDialogContentText = styled(DialogContent)`
  word-break: break-word;

  // React markdown shrinks the text size by wrapping it in p tags.
  // This resets it to what it would be normally.
  p {
    font-size: 1rem;
  }
`;

export type ConfirmHookProps = {
  readonly disableOk: boolean;
  readonly handleCancel: () => void;
  readonly handleChangeConfirmText: ChangeEventHandler<HTMLInputElement>;
  readonly handleConfirm: () => void;
} & ConfirmState;

export const Confirm: React.FC<ConfirmHookProps> = (props) => {
  const {
    confirmText,
    open,
    title,
    hideCancel,
    okLabel,
    cancelLabel,
    type,
    message,
    disableOk,
    handleCancel,
    handleChangeConfirmText,
    handleConfirm
  } = props;

  const OkButton = type === 'delete' ? DeleteButton : Button;

  return (
    <ConfirmDialog open={open} onClose={handleCancel}>
      <DialogTitle>{title}</DialogTitle>
      <StyledDialogContentText>
        {message && <ReactMarkdown escapeHtml source={message} />}
        {confirmText && <ConfirmInput title="Confirm" onChange={handleChangeConfirmText} placeholder={confirmText} />}
      </StyledDialogContentText>
      <DialogActions>
        {!hideCancel && (
          <Button variant="outlined" onClick={handleCancel}>
            {cancelLabel}
          </Button>
        )}
        <OkButton variant="contained" color="primary" onClick={handleConfirm} disabled={disableOk}>
          {okLabel}
        </OkButton>
      </DialogActions>
    </ConfirmDialog>
  );
};
